import React, { ReactNode } from 'react';
import { Translate, I18n } from 'react-redux-i18n';
import { Field, Form } from 'react-final-form';
import { GenderEnum } from '../../../../network/api/types/compte/ListMember';
import { InvitationBasicDTO } from '../../../../network/api/types/InvitationDTO';
import {
  required,
  validateEmail,
  validateName,
} from '../../../../services/utils';
import Button from '../../../common/Button';
import H4Title from '../../../common/H4Title';
import TextInput from '../../../common/inputs/TextInput';
import Modal from '../../../common/modals/Modal';

type InvitationFormValues = {
  gender: string,
  firstName: string,
  lastName: string,
  email: string,
};

type Props = {
  show: boolean;
  close: () => void;
  createInvitation: (invitationToSave: InvitationBasicDTO) => void;
  isLoading: boolean;
};

class InvitationModal extends React.Component<Props> {
  submitInvitationForm = (values: InvitationFormValues): void => {
    const newInvitation: InvitationBasicDTO = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      gender: values.gender,
    };
    this.props.createInvitation(newInvitation);
  };

  getGenderInputCssClass = (checked: boolean, error: boolean, touched: boolean): string => {
    let basicClass = 'button-customize';
    if (checked) {
      basicClass += ' checked';
    }
    if (error && touched) {
      basicClass += ' error-custom';
    }
    return basicClass;
  };

  composeValidators = (
    ...validators: ((value: string | number | boolean) => string | undefined)[]
  ) =>
    (value: string | number | boolean): string | undefined =>
      validators.reduce(
        (error: string | undefined, validator) => error || validator(value), undefined,
      );

  render(): ReactNode {
    return (
      <>
        <Modal
          isOpen={this.props.show}
          className="invitation-modal"
        >
          <div className="invitation-modal-container">
            <H4Title>
              {I18n.t('collaborators.invitation.popin.title')}
            </H4Title>
            <div className="text">{I18n.t('collaborators.invitation.popin.desc')}</div>
            <Form
              onSubmit={this.submitInvitationForm}
              render={(
                {
                  handleSubmit,
                  valid,
                },
              ) =>
                (
                  <form
                    className="invitation-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSubmit(e);
                    }}
                  >
                    <div>
                      <Field
                        type="radio"
                        name="gender"
                        value={GenderEnum.MALE}
                        validate={required}
                        render={({ input, meta }) => (
                          <label
                            htmlFor="male"
                            className="big-button-radio"
                          >
                            <input
                              type="radio"
                              name={input.gender}
                              id="male"
                              value={GenderEnum.MALE}
                              checked={input.checked}
                              onChange={input.onChange}
                            />
                            <Translate
                              className={this.getGenderInputCssClass(
                                input.checked, meta.error, meta.touched,
                              )}
                              value="GENDER.male"
                            />
                          </label>
                        )}
                      />
                      <Field
                        type="radio"
                        name="gender"
                        value={GenderEnum.FEMALE}
                        validate={required}
                        render={({ input, meta }) => (
                          <label
                            htmlFor="female"
                            className="big-button-radio"
                          >
                            <input
                              type="radio"
                              name={input.name}
                              id="female"
                              value={GenderEnum.FEMALE}
                              checked={input.checked}
                              onChange={input.onChange}
                            />
                            <Translate
                              className={this.getGenderInputCssClass(
                                input.checked, meta.error, meta.touched,
                              )}
                              value="GENDER.female"
                            />
                          </label>
                        )}
                      />
                    </div>
                    <Field
                      name="lastName"
                      component={TextInput}
                      placeholder={I18n.t('collaborators.invitation.form.lastname')}
                      type="text"
                      validate={this.composeValidators(required, validateName)}
                    />
                    <Field
                      name="firstName"
                      component={TextInput}
                      placeholder={I18n.t('collaborators.invitation.form.firstname')}
                      type="text"
                      validate={this.composeValidators(required, validateName)}
                    />
                    <Field
                      name="email"
                      component={TextInput}
                      placeholder={I18n.t('collaborators.invitation.form.email')}
                      type="text"
                      validate={this.composeValidators(required, validateEmail)}
                    />
                    <div className="text text--footer">{I18n.t('collaborators.invitation.popin.warning')}</div>
                    <div className="invitation-footer">
                      <Button
                        type="third"
                        i18nKey="actions.CANCEL"
                        onClick={this.props.close}
                      />
                      <Button
                        type="primary"
                        buttonType="submit"
                        i18nKey="collaborators.invitation.popin.send"
                        className={`invitation ${valid ? '' : 'disabled'}`}
                        isLoading={this.props.isLoading}
                      />
                    </div>
                  </form>
                )}
            />
          </div>
        </Modal>
      </>
    );
  }
}

export default InvitationModal;
